import React from 'react';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformHubCard } from '../../../components/AgentPlatform/AgentPlatformHubCard';
import { AgentPlatformHubHeroIcon } from '../../../components/AgentPlatform/AgentPlatformHubHeroIcon';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { useAgentPlatformAccess } from '../../../hooks/useAgentPlatformAccess';
import { apTheme } from '../../../utils/agentPlatformTheme';
import type { HubCardIconTone } from '../../../utils/agentPlatformTheme';

import { agentPlatformMenuIconPaths } from '../../../constants/agentPlatformMenuIcons';
import { AgentPlatformHubRobotIcon } from '../../../components/AgentPlatform/AgentPlatformHubRobotIcon';

const HubIcon = ({ icon }: { icon: keyof typeof agentPlatformMenuIconPaths }) => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d={agentPlatformMenuIconPaths[icon]} />
  </svg>
);

type HubCardConfig = {
  title: string;
  description: string;
  to: string;
  enabled: boolean;
  iconTone: HubCardIconTone;
  icon: React.ReactNode;
};

export const AgentPlatformHubPage: React.FC = () => {
  const access = useAgentPlatformAccess();

  const cards: HubCardConfig[] = [
    {
      title: 'Assistente de IA',
      description:
        'Defina prompts, vínculos e teste conversas no playground integrado.',
      to: '/agent-platform/agents',
      enabled: access.hasAgents,
      iconTone: 'red',
      icon: <AgentPlatformHubRobotIcon />,
    },
    {
      title: 'Fluxos de Trabalho',
      description:
        'Editor visual (canvas) para criação de fluxos de automação, com versionamento e publicação controlada.',
      to: '/agent-platform/flows',
      enabled: access.hasFlows,
      iconTone: 'stone',
      icon: <HubIcon icon="flows" />,
    },
    {
      title: 'Base de Conhecimento',
      description: 'Cadastre bases para RAG e associe conteúdo aos agentes de orquestração.',
      to: '/agent-platform/knowledge-bases',
      enabled: access.hasAgents,
      iconTone: 'purple',
      icon: <HubIcon icon="knowledgeBase" />,
    },
    {
      title: 'Ferramentas',
      description: 'Catálogo de integrações HTTP e conectores vinculáveis aos agentes.',
      to: '/agent-platform/tools',
      enabled: access.hasTools,
      iconTone: 'slate',
      icon: <HubIcon icon="tools" />,
    },
    {
      title: 'Instâncias de Automação',
      description: 'Registre executores SaaS ou dedicados e vincule-os aos fluxos do composer.',
      to: '/agent-platform/automation-instances',
      enabled: access.hasFlows,
      iconTone: 'amber',
      icon: <HubIcon icon="automationInstance" />,
    },
    {
      title: 'Monitoria',
      description: 'Acompanhe execuções, consumo de tokens e políticas globais de observabilidade.',
      to: '/agent-platform/monitoring',
      enabled: access.hasPlatform,
      iconTone: 'zinc',
      icon: <HubIcon icon="monitoring" />,
    },
    {
      title: 'Execuções',
      description: 'Histórico detalhado, status, tokens e linha do tempo de cada execução.',
      to: '/agent-platform/executions',
      enabled: access.hasPlatform,
      iconTone: 'sky',
      icon: <HubIcon icon="executions" />,
    },
    {
      title: 'Governança',
      description: 'Auditoria, políticas de monitoria e controles de conformidade operacional.',
      to: '/agent-platform/governance',
      enabled: access.hasPlatform,
      iconTone: 'green',
      icon: <HubIcon icon="governance" />,
    },
  ];

  return (
    <AgentPlatformFeatureGate feature="platform">
      <AgentPlatformAdminGate>
        <AgentPlatformShell variant="hub" contentClassName="max-w-none px-0 py-0">
          <div className={apTheme.hero.hub}>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-6 lg:gap-12">
              <div className="flex-1 min-w-0 py-2">
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Plataforma Agêntica</h1>
                <p className={`mt-3 max-w-2xl text-base leading-relaxed ${apTheme.hero.subtitle}`}>
                  Orquestração enterprise de assistentes de IA, agentes, fluxos de automação e
                  observabilidade centralizada — agnóstica ao runtime de automação do seu ambiente.
                </p>
              </div>
              <div
                className="hidden sm:flex shrink-0 items-center justify-center pr-2 lg:pr-6"
                aria-hidden
              >
                <AgentPlatformHubHeroIcon className="h-28 w-28 lg:h-36 lg:w-36 text-[#c0392b]" />
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 pb-12">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map(card => (
                <AgentPlatformHubCard key={card.to} {...card} />
              ))}
            </div>
          </div>
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
